// Moves focus to the new route's <h1> on every navigation, so a screen
// reader announces the view the user just arrived at instead of leaving focus
// on the link they clicked. Required by
// docs/canonical/responsive-web-ui-system.md §8.
//
// Two things this has to get right, and the previous version got both wrong:
//
//   1. It must focus the NEW heading. The old implementation attached a
//      "settled" listener to whatever <h1> was mounted when the effect ran --
//      which, on a client-side navigation, is still the OLD route's heading.
//      It focused that, marked itself settled, disconnected its observer, and
//      never touched the incoming heading. The old node then unmounted and
//      focus fell back to <body>, so the announcement never happened.
//      This version focuses after React has committed the new route (two
//      animation frames), which is the only reliable signal -- node identity
//      cannot distinguish them, because React reuses the same <h1> element
//      across routes.
//
//   2. It must not look like selected text. The heading is given
//      tabindex="-1" so it can receive focus programmatically; the focus ring
//      is scoped in tokens.css to elements a keyboard can actually reach, so
//      this focus is silent to sighted users and audible to assistive tech.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/** Give up after this long rather than watching the DOM forever. */
const OBSERVE_TIMEOUT_MS = 3000;

export function RouteHeadingFocus() {
  const location = useLocation();

  useEffect(() => {
    let done = false;
    let raf1 = 0;
    let raf2 = 0;

    const selectRouteHeading = (): HTMLElement | null => {
      const shell = document.querySelector('main[data-testid="shell-main"]');
      return shell
        ? shell.querySelector('h1')
        : document.querySelector('main h1, h1');
    };

    const focusHeading = (): boolean => {
      if (done) return true;
      const heading = selectRouteHeading();
      if (!(heading instanceof HTMLElement)) return false;
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      done = true;

      // A lazily loaded route renders a fallback first, so the heading we just
      // focused can be replaced a frame later -- React drops focus to <body>
      // and the announcement is lost. Verify once and retry if that happened.
      window.requestAnimationFrame(() => {
        if (document.activeElement !== document.body) return;
        const settled = selectRouteHeading();
        if (settled instanceof HTMLElement) {
          if (!settled.hasAttribute('tabindex')) settled.setAttribute('tabindex', '-1');
          settled.focus({ preventScroll: true });
        }
      });
      return true;
    };

    // Two frames: the first lets React commit the new route, the second runs
    // after paint, so the heading being focused is the incoming one. A single
    // frame can still see the outgoing heading. Node identity cannot be used
    // to tell them apart -- React reuses the same <h1> element across routes,
    // so the check has to be "after commit", not "a different node".
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        focusHeading();
      });
    });

    // A lazily loaded route chunk may not have rendered its heading yet.
    const observer = new MutationObserver(() => {
      if (focusHeading()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timeout = window.setTimeout(() => observer.disconnect(), OBSERVE_TIMEOUT_MS);

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(timeout);
      observer.disconnect();
    };
  }, [location.pathname]);

  return null;
}

export default RouteHeadingFocus;
